import type { MenuItemType } from "#src/layout/layout-menu/types";
import type { AppRouteRecordRaw } from "#src/router/types";
import type { ReactNode } from "react";

import { menuIcons } from "#src/icons/menu-icons";
import { isString } from "#src/utils/is";
import { createElement } from "react";
import { Link } from "react-router";

import { removeTrailingSlash } from "./remove-trailing-slash";

/** Prefix for menu keys when a route has no `path` (layout-only nodes). Not a navigable URL. */
export const SYNTHETIC_MENU_KEY_PREFIX = "__mg:" as const;

export function isSyntheticMenuKey(key: string): boolean {
	return key.startsWith(SYNTHETIC_MENU_KEY_PREFIX);
}

function slugFromTitle(title: ReactNode | undefined, index: number): string {
	if (typeof title === "string" || typeof title === "number") {
		const slug = String(title)
			.trim()
			.replaceAll(/\s+/g, "-")
			.replaceAll(/[^\w-]/g, "")
			.slice(0, 48);
		return slug.length > 0 ? slug : `i${index}`;
	}
	return `i${index}`;
}

function resolveMenuItemKey(
	item: AppRouteRecordRaw,
	parentMenuKey: string | undefined,
	index: number,
): string {
	const rawPath = "path" in item && item.path ? String(item.path).trim() : "";
	if (rawPath.length > 0) {
		return removeTrailingSlash(rawPath);
	}
	const parent = parentMenuKey ?? "root";
	return `${SYNTHETIC_MENU_KEY_PREFIX}${parent}:${index}:${slugFromTitle(item.handle?.title, index)}`;
}

/**
 * Generate an array of menu items from the route list
 *
 * @param routeList The route list, an array of type AppRouteRecordRaw
 * @param parentMenuKey Resolved key of the parent menu item (for synthetic keys on pathless routes)
 * @returns Returns an array of menu items, where array elements are of type MenuItemType
 */
export function generateMenuItemsFromRoutes(
	routeList: AppRouteRecordRaw[],
	parentMenuKey?: string,
) {
	return routeList.reduce<MenuItemType[]>((acc, item, index) => {
		const label = item.handle?.title;
		const externalLink = item?.handle?.externalLink;
		const iconName = item?.handle?.icon;

		const key = resolveMenuItemKey(item, parentMenuKey, index);

		const menuItem: MenuItemType = {
			key,
			label: externalLink
				? createElement(
					Link,
					{
						// Stop event bubbling to prevent triggering the menu click event
						onClick: (e) => {
							e.stopPropagation();
						},
						to: externalLink,
						target: "_blank",
						rel: "noopener noreferrer",
					},
					label,
				)
				: (
					label
				),
		};
		if (iconName) {
			menuItem.icon = iconName;
			if (isString(iconName)) {
				if (menuIcons[iconName]) {
					menuItem.icon = createElement(menuIcons[iconName]);
				} else {
					console.warn(
						`menu-icon: icon "${iconName}" not found in src/icons/menu-icons.ts file`,
					);
				}
			}
		}
		if (Array.isArray(item.children) && item.children.length > 0) {
			// Filter out non-index routes and routes hidden in the menu
			const noIndexRoute = item.children.filter(route => !route.index && !route?.handle?.hideInMenu);
			if (noIndexRoute.length > 0) {
				menuItem.children = generateMenuItemsFromRoutes(noIndexRoute, menuItem.key);
			}
		}
		if (item?.handle?.hideInMenu) {
			return acc;
		}
		return [...acc, menuItem];
	}, []);
}
